import React, { useState, useEffect } from 'react';
import { App, Modal, Form, Input, Select, Checkbox, Button, Typography, Alert, Divider, Space, List } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import type { OrderItem, CancelOrderItemRequest, CancellationReason } from '../../../types/orders';
import type { User } from '../../../types/auth';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface CancellationModalProps {
  visible: boolean;
  orderItems: OrderItem[];
  onCancel: () => void;
  onConfirm: (cancellations: CancelOrderItemRequest[]) => Promise<void>;
  loading?: boolean;
  currentUser?: User;
}

// Predefined cancellation reasons
const CANCELLATION_REASONS: CancellationReason[] = [
  { id: 'customer-request', label: 'Customer Request', requiresNotes: false },
  { id: 'out-of-stock', label: 'Out of Stock', requiresNotes: false },
  { id: 'preparation-error', label: 'Preparation Error', requiresNotes: true },
  { id: 'quality-issue', label: 'Quality Issue', requiresNotes: true },
  { id: 'wrong-order', label: 'Wrong Order', requiresNotes: false },
  { id: 'kitchen-delay', label: 'Kitchen Delay', requiresNotes: true },
  { id: 'other', label: 'Other', requiresNotes: true }
];

export const CancellationModal: React.FC<CancellationModalProps> = ({
  visible,
  orderItems,
  onCancel,
  onConfirm,
  loading = false,
  currentUser
}) => {
  // App.useApp() y no el import estatico: bajo React 19 el estatico no dibuja
  const { modal } = App.useApp();
  const [form] = Form.useForm();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [requiresNotes, setRequiresNotes] = useState(false);
  const [authorizedUserId, setAuthorizedUserId] = useState<string>('');
  const [globalNotes, setGlobalNotes] = useState<string>('');

  // Filter out already cancelled items
  const availableItems = orderItems.filter(item => !item.isCancelled);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setSelectedItems([]);
      setSelectedReason('');
      setRequiresNotes(false);
      setAuthorizedUserId('');
      setGlobalNotes('');
    }
  }, [visible, form]);

  useEffect(() => {
    const reason = CANCELLATION_REASONS.find(r => r.id === selectedReason);
    setRequiresNotes(reason?.requiresNotes || false);
  }, [selectedReason]);

  const handleItemSelection = (itemId: string, checked: boolean) => {
    setSelectedItems(prev => 
      checked 
        ? [...prev, itemId]
        : prev.filter(id => id !== itemId)
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedItems(checked ? availableItems.map(item => item.id) : []);
  };

  const calculateRefundAmount = () => {
    return selectedItems.reduce((total, itemId) => {
      const item = availableItems.find(i => i.id === itemId);
      return total + (item?.total || 0);
    }, 0);
  };

  const handleConfirm = async () => {
    try {
      await form.validateFields();
      
      if (selectedItems.length === 0) {
        modal.warning({
          title: 'No Items Selected',
          content: 'Please select at least one item to cancel.',
        });
        return;
      }

      if (!selectedReason) {
        modal.warning({
          title: 'Reason Required',
          content: 'Please select a cancellation reason.',
        });
        return;
      }

      if (!authorizedUserId) {
        modal.warning({
          title: 'Authorization Required',
          content: 'Please provide the user ID for authorization.',
        });
        return;
      }

      if (requiresNotes && !globalNotes.trim()) {
        modal.warning({
          title: 'Notes Required',
          content: 'This cancellation reason requires additional notes.',
        });
        return;
      }

      // Create cancellation requests
      const cancellations: CancelOrderItemRequest[] = selectedItems.map(itemId => ({
        OrderItemId: itemId,
        Reason: selectedReason,
        Notes: globalNotes.trim() || undefined,
        AuthorizedByUserId: currentUser?.id || authorizedUserId || 'current-user'
      }));

      await onConfirm(cancellations);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const selectedReason_obj = CANCELLATION_REASONS.find(r => r.id === selectedReason);

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#faad14' }} />
          <span>Cancel Order Items</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button 
          key="confirm" 
          type="primary" 
          danger 
          onClick={handleConfirm}
          loading={loading}
          disabled={selectedItems.length === 0}
        >
          Confirm Cancellation
        </Button>
      ]}
      width={600}
      destroyOnClose
    >
      <Alert
        message="Warning"
        description="Cancelling order items cannot be undone. Please ensure authorization before proceeding."
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form form={form} layout="vertical">
        {/* Item Selection */}
        <Form.Item label="Select Items to Cancel">
          <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border-color-strong)', borderRadius: 6, padding: 8 }}>
            <Checkbox
              checked={selectedItems.length === availableItems.length && availableItems.length > 0}
              indeterminate={selectedItems.length > 0 && selectedItems.length < availableItems.length}
              onChange={(e) => handleSelectAll(e.target.checked)}
              style={{ marginBottom: 8, fontWeight: 'bold' }}
            >
              Select All Items
            </Checkbox>
            <Divider style={{ margin: '8px 0' }} />
            <List
              dataSource={availableItems}
              renderItem={(item) => (
                <List.Item style={{ padding: '4px 0' }}>
                  <Checkbox
                    checked={selectedItems.includes(item.id)}
                    onChange={(e) => handleItemSelection(item.id, e.target.checked)}
                  >
                    <Space direction="vertical" size={0}>
                      <Text strong>{item.productName}</Text>
                      <Text type="secondary">
                        Qty: {item.quantity} × ${item.unitPrice.toFixed(2)} = ${item.total.toFixed(2)}
                      </Text>
                      {item.notes && (
                        <Text type="secondary" italic>
                          Note: {item.notes}
                        </Text>
                      )}
                      {item.variants && item.variants.length > 0 && (
                        <Text type="secondary">
                          Variants: {item.variants.map(v => v.variantName).join(', ')}
                        </Text>
                      )}
                    </Space>
                  </Checkbox>
                </List.Item>
              )}
            />
          </div>
        </Form.Item>

        {/* Cancellation Reason */}
        <Form.Item 
          label="Cancellation Reason"
          name="reason"
          rules={[{ required: true, message: 'Please select a cancellation reason' }]}
        >
          <Select
            placeholder="Select a reason for cancellation"
            value={selectedReason}
            onChange={setSelectedReason}
          >
            {CANCELLATION_REASONS.map(reason => (
              <Option key={reason.id} value={reason.id}>
                {reason.label}
                {reason.requiresNotes && <Text type="secondary"> (notes required)</Text>}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Notes */}
        <Form.Item 
          label="Additional Notes"
          name="notes"
          rules={requiresNotes ? [{ required: true, message: 'Notes are required for this cancellation reason' }] : []}
        >
          <TextArea
            placeholder={requiresNotes ? "Please provide additional details (required)" : "Optional additional information"}
            rows={3}
            value={globalNotes}
            onChange={(e) => setGlobalNotes(e.target.value)}
          />
        </Form.Item>

        {/* Authorization */}
        <Form.Item 
          label="Authorized By (User ID)"
          name="authorizedBy"
          rules={[{ required: true, message: 'Authorization is required for cancellations' }]}
          extra="Enter the User ID of the manager or supervisor authorizing this cancellation"
        >
          <Input
            placeholder="Enter authorizing user ID"
            value={authorizedUserId}
            onChange={(e) => setAuthorizedUserId(e.target.value)}
          />
        </Form.Item>
      </Form>

      {/* Summary */}
      {selectedItems.length > 0 && (
        <>
          <Divider />
          <div style={{ backgroundColor: 'var(--bg-secondary)', padding: 12, borderRadius: 6 }}>
            <Title level={5} style={{ margin: 0, marginBottom: 8 }}>Cancellation Summary</Title>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Items to cancel:</Text>
                <Text strong>{selectedItems.length}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>Total refund amount:</Text>
                <Text strong style={{ color: 'var(--color-error-text)' }}>
                  ${calculateRefundAmount().toFixed(2)}
                </Text>
              </div>
              {selectedReason_obj && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text>Reason:</Text>
                  <Text>{selectedReason_obj.label}</Text>
                </div>
              )}
            </Space>
          </div>
        </>
      )}
    </Modal>
  );
};